/*
 * <eu-carousel> — shared slider for the 2026 redesign.
 * Used by eu-hero and eu-testimonials. No dependencies.
 *
 * Markup contract:
 *   <eu-carousel data-autoplay="5000" data-loop>
 *     <div data-eu-track>
 *       <div data-eu-slide>…</div>
 *     </div>
 *     <button data-eu-prev>  <button data-eu-next>   (optional)
 *     <div data-eu-dots>                              (optional, auto-filled)
 *   </eu-carousel>
 */
(() => {
  // Scoped: several sections include this file, and a second top-level
  // `class` declaration would throw "Identifier 'EuCarousel' has already been declared".
  if (customElements.get('eu-carousel')) return;

class EuCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-eu-track]');
    this.slides = Array.from(this.querySelectorAll('[data-eu-slide]'));
    if (!this.track || this.slides.length === 0) return;

    this.index = 0;
    this.loop = this.hasAttribute('data-loop');
    this.interval = parseInt(this.getAttribute('data-autoplay') || '0', 10);
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.buildDots();
    this.bind();
    this.goTo(0, { focus: false });

    if (this.interval > 0 && !this.reduced && this.slides.length > 1) this.play();

    // Theme editor: show the slide whose block is selected, as the theme's
    // own slideshow does, and hold there while it is selected. The events
    // fire on the block element, which is the slide (block.shopify_attributes).
    if (window.Shopify?.designMode) {
      this.onBlockSelect = (e) => {
        const i = this.slides.indexOf(e.target);
        if (i === -1) return;
        this.pause();
        this.goTo(i, { focus: false });
      };
      this.onBlockDeselect = (e) => {
        if (!this.slides.includes(e.target)) return;
        if (this.interval > 0 && !this.reduced && this.slides.length > 1) this.play();
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    }
  }

  disconnectedCallback() {
    this.pause();
    if (this.onBlockSelect) {
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }
  }

  buildDots() {
    this.dotsEl = this.querySelector('[data-eu-dots]');
    if (!this.dotsEl || this.slides.length < 2) return;
    this.dots = this.slides.map((_, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'eu-carousel__dot';
      b.setAttribute('aria-label', `Go to slide ${i + 1} of ${this.slides.length}`);
      b.addEventListener('click', () => {
        this.pause();
        this.goTo(i);
      });
      this.dotsEl.appendChild(b);
      return b;
    });
  }

  bind() {
    this.prevEl = this.querySelector('[data-eu-prev]');
    this.nextEl = this.querySelector('[data-eu-next]');
    this.prevEl?.addEventListener('click', () => { this.pause(); this.step(-1); });
    this.nextEl?.addEventListener('click', () => { this.pause(); this.step(1); });

    // Arrow keys when focus is inside the carousel.
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { this.pause(); this.step(-1); }
      if (e.key === 'ArrowRight') { this.pause(); this.step(1); }
    });

    // Autoplay should never run past a user's attention.
    this.addEventListener('mouseenter', () => this.pause());
    this.addEventListener('focusin', () => this.pause());
  }

  step(delta) {
    let next = this.index + delta;
    if (next < 0) next = this.loop ? this.slides.length - 1 : 0;
    if (next >= this.slides.length) next = this.loop ? 0 : this.slides.length - 1;
    this.goTo(next);
  }

  goTo(i, { focus = true } = {}) {
    this.index = i;
    this.slides.forEach((s, n) => {
      const active = n === i;
      s.classList.toggle('is-active', active);
      // Keep inactive slides out of the a11y tree and tab order.
      s.setAttribute('aria-hidden', active ? 'false' : 'true');
      s.inert = !active;
    });
    this.dots?.forEach((d, n) => {
      d.classList.toggle('is-active', n === i);
      d.setAttribute('aria-current', n === i ? 'true' : 'false');
    });
    if (this.prevEl && !this.loop) this.prevEl.disabled = i === 0;
    if (this.nextEl && !this.loop) this.nextEl.disabled = i === this.slides.length - 1;
    if (focus) this.dispatchEvent(new CustomEvent('eu:slidechange', { detail: { index: i } }));
  }

  play() {
    this.pause();
    this.timer = setInterval(() => this.step(1), this.interval);
  }

  pause() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }
}

customElements.define('eu-carousel', EuCarousel);
})();
